import { useState, useEffect } from "react";
import { IconButton } from "@mui/material";
import { Bookmark } from "lucide-react";
import axios from "utils/axios";
import { openSnackbar } from "api/snackbar";
import { ACTION_ICON_SIZES } from "components/ActionIconButton";

const FavoriteButton = ({
  id,
  type = "news",
  initialIsFavorite = false,
  onFavoriteChange,
  size = "medium",
  isOnFavoritesPage = false,
}) => {
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  useEffect(() => {
    setIsFavorite(initialIsFavorite);
  }, [initialIsFavorite]);

  const handleFavoriteToggle = async (e) => {
    e && e.stopPropagation();

    if (isTogglingFavorite) return;

    setIsTogglingFavorite(true);

    try {
      const newFavoriteState = !isFavorite;

      if (isOnFavoritesPage && isFavorite && onFavoriteChange) {
        onFavoriteChange(id);
        return;
      }

      setIsFavorite(newFavoriteState);

      const response = await axios.post("/api/users/favorites/toggle/", {
        id: id,
        type: type,
      });

      if (response.status === 200 || response.status === 201) {
        openSnackbar({
          message: response.data.message,
          open: true,
          variant: "alert",
          alert: { color: "success" },
          autoHideDuration: 6000,
        });

        if (onFavoriteChange) {
          onFavoriteChange(id, newFavoriteState);
        }
      } else {
        setIsFavorite(!newFavoriteState);
        openSnackbar({
          message: "Erreur lors de la modification des favoris",
          open: true,
          variant: "alert",
          alert: { color: "error" },
        });
      }
    } catch (error) {
      setIsFavorite(isFavorite);

      openSnackbar({
        message: "Erreur lors de la modification des favoris",
        open: true,
        variant: "alert",
        alert: { color: "error", variant: "filled" },
      });
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const dims = ACTION_ICON_SIZES[size] || ACTION_ICON_SIZES.medium;

  return (
    <IconButton
      onClick={handleFavoriteToggle}
      disabled={isTogglingFavorite}
      className="v2-action-btn"
      sx={{
        bgcolor: isFavorite ? "var(--brand-accent)" : "var(--brand-surface)",
        width: dims.box,
        height: dims.box,
        borderRadius: "var(--brand-radius-md)",
        border: isFavorite
          ? "1px solid var(--brand-accent)"
          : "1px solid var(--brand-line)",
        boxShadow: "none",
        color: isFavorite ? "var(--brand-on-accent)" : "var(--brand-accent)",
        "&:hover": {
          bgcolor: isFavorite
            ? "var(--brand-accent-hover)"
            : "var(--brand-accent-soft)",
          borderColor: "var(--brand-accent-border)",
          boxShadow: "none",
        },
      }}
    >
      <Bookmark
        color="currentColor"
        fill={isFavorite ? "currentColor" : "none"}
        size={dims.icon}
        strokeWidth={2}
      />
    </IconButton>
  );
};

export default FavoriteButton;
